"use client";

import * as React from "react";
import { motion, AnimatePresence } from "motion/react";

import { cn } from "@ocr-monorepo/ui/lib/utils";

type StepperOrientation = "vertical" | "horizontal";

type StepperActions = {
  onNext?: () => void;
  onBack?: () => void;
  disableNext?: boolean;
  disableBack?: boolean;
};

type StepperContextValue = {
  orientation: StepperOrientation;
  actions: StepperActions;
  setActions: (actions: StepperActions) => void;
  direction: number;
  setDirection: (direction: number) => void;
};

const StepperContext = React.createContext<StepperContextValue | null>(null);

const SPRING_TRANSITION = {
  type: "spring" as const,
  stiffness: 300,
  damping: 30,
  mass: 1,
};

function useStepper() {
  const context = React.useContext(StepperContext);
  if (!context) {
    throw new Error("useStepper must be used within StepperProvider.");
  }

  return context;
}

/**
 * StepperProvider - Provides stepper orientation and action handlers
 */
const StepperProvider = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    orientation?: StepperOrientation;
  }
>(({ className, orientation = "vertical", children, ...props }, ref) => {
  const [actions, setActionsState] = React.useState<StepperActions>({});
  const [direction, setDirectionState] = React.useState(0);

  // Stable callback refs to avoid triggering useEffect re-runs in consumers
  const setActions = React.useCallback((newActions: StepperActions) => {
    setActionsState(newActions);
  }, []);

  const setDirection = React.useCallback((newDirection: number) => {
    setDirectionState(newDirection);
  }, []);

  const contextValue = React.useMemo(
    () => ({ orientation, actions, setActions, direction, setDirection }),
    [orientation, actions, setActions, direction, setDirection],
  );

  return (
    <StepperContext.Provider value={contextValue}>
      <div
        ref={ref}
        data-slot="stepper-provider"
        data-orientation={orientation}
        className={cn(
          "flex",
          orientation === "vertical" ? "flex-col" : "flex-row items-center",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </StepperContext.Provider>
  );
});
StepperProvider.displayName = "StepperProvider";

/**
 * Stepper - The root container for steps
 * For vertical layout: renders steps in a column
 * For horizontal layout: renders steps in a row
 */
const Stepper = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useStepper();

  return (
    <div
      ref={ref}
      data-slot="stepper"
      data-orientation={orientation}
      className={cn(
        "flex",
        orientation === "vertical" ? "flex-col" : "flex-row items-center",
        className,
      )}
      {...props}
    />
  );
});
Stepper.displayName = "Stepper";

/**
 * StepperItem - Container for a single step
 * Contains the indicator column (indicator + connector) and text column (title + description)
 */
const StepperItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useStepper();

  return (
    <div
      ref={ref}
      data-slot="stepper-item"
      data-orientation={orientation}
      className={cn(
        "group flex",
        orientation === "vertical"
          ? "flex-row gap-3"
          : "flex-col items-center gap-2",
        className,
      )}
      {...props}
    />
  );
});
StepperItem.displayName = "StepperItem";

/**
 * StepperIndicator - The animated circle showing step number or checkmark
 */
const StepperIndicator = React.forwardRef<
  HTMLDivElement,
  Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "onDrag" | "onDragStart" | "onDragEnd" | "onAnimationStart"
  > & {
    state?: "default" | "active" | "completed";
  }
>(({ className, state = "default", children, ...props }, ref) => {
  const isActive = state === "active";
  const isCompleted = state === "completed";
  const isPastOrActive = isActive || isCompleted;

  return (
    <motion.div
      ref={ref}
      data-slot="stepper-indicator"
      data-state={state}
      initial={false}
      animate={{
        scale: isActive ? 1.1 : 1,
        backgroundColor: isPastOrActive
          ? "var(--primary)"
          : "var(--background)",
        borderColor: isPastOrActive
          ? "var(--primary)"
          : "color-mix(in oklch, var(--primary) 30%, transparent)",
        color: isPastOrActive
          ? "var(--primary-foreground)"
          : "color-mix(in oklch, var(--primary) 50%, transparent)",
      }}
      transition={SPRING_TRANSITION}
      className={cn(
        "relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold tabular-nums",
        className,
      )}
      {...props}
    >
      {children}
    </motion.div>
  );
});
StepperIndicator.displayName = "StepperIndicator";

/**
 * StepperConnector - The animated line connecting steps
 * For vertical stepper: renders as a vertical line below the indicator
 * For horizontal stepper: renders as a horizontal line between indicators
 */
const StepperConnector = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    completed?: boolean;
  }
>(({ className, completed, ...props }, ref) => {
  const { orientation } = useStepper();
  const isVertical = orientation === "vertical";

  return (
    <div
      ref={ref}
      data-slot="stepper-connector"
      data-completed={completed ? "true" : "false"}
      data-orientation={orientation}
      className={cn(
        "relative my-1",
        isVertical ? "w-0.5 flex-1 min-h-6" : "h-0.5 flex-1 min-w-6",
        "bg-primary/20",
        className,
      )}
      {...props}
    >
      <motion.div
        className={cn(
          "absolute inset-0 bg-primary",
          isVertical ? "origin-top" : "origin-left",
        )}
        initial={false}
        animate={{
          scaleY: isVertical ? (completed ? 1 : 0) : 1,
          scaleX: isVertical ? 1 : completed ? 1 : 0,
        }}
        transition={SPRING_TRANSITION}
      />
    </div>
  );
});
StepperConnector.displayName = "StepperConnector";

/**
 * StepperTitle - The title text for a step
 */
const StepperTitle = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & {
    active?: boolean;
  }
>(({ className, active, ...props }, ref) => (
  <span
    ref={ref}
    data-slot="stepper-title"
    className={cn(
      "text-sm font-semibold transition-colors duration-200",
      active ? "text-primary" : "text-primary/50",
      className,
    )}
    {...props}
  />
));
StepperTitle.displayName = "StepperTitle";

/**
 * StepperDescription - The description text for a step
 */
const StepperDescription = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    data-slot="stepper-description"
    className={cn("text-xs text-foreground", className)}
    {...props}
  />
));
StepperDescription.displayName = "StepperDescription";

/**
 * StepperContent - Animated content wrapper for step transitions
 */
const contentVariants = {
  enter: (direction: number) => ({
    y: direction > 0 ? 15 : -15,
    opacity: 0,
    filter: "blur(4px)",
  }),
  center: {
    zIndex: 1,
    y: 0,
    opacity: 1,
    filter: "blur(0px)",
  },
  exit: (direction: number) => ({
    zIndex: 0,
    y: direction < 0 ? 15 : -15,
    opacity: 0,
    filter: "blur(4px)",
    position: "absolute" as const,
  }),
};

const StepperContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    stepKey: string | number;
  }
>(({ className, stepKey, children, ...props }, ref) => {
  const { direction } = useStepper();

  return (
    <div className="relative" {...props}>
      <AnimatePresence mode="popLayout" custom={direction} initial={false}>
        <motion.div
          ref={ref}
          key={stepKey}
          custom={direction}
          variants={contentVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            y: SPRING_TRANSITION,
            opacity: { duration: 0.2 },
            filter: { duration: 0.2 },
          }}
          className={cn("w-full", className)}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
});
StepperContent.displayName = "StepperContent";

export {
  StepperProvider,
  Stepper,
  StepperItem,
  StepperIndicator,
  StepperConnector,
  StepperTitle,
  StepperDescription,
  StepperContent,
  useStepper,
};
